import React, { useState, useEffect } from 'react';
import Slider from '../components/Slider';
import client from '../api/client';

const COMPLICATION_OPTIONS = [
  { key: 'fever', label: 'Fever' },
  { key: 'excessive_bleeding', label: 'Excessive Bleeding' },
  { key: 'urinary_retention', label: 'Urinary Retention' },
  { key: 'nausea_vomiting', label: 'Nausea/Vomiting' },
  { key: 'constipation', label: 'Constipation' },
  { key: 'infection_signs', label: 'Infection Signs' },
  { key: 'er_visit', label: 'ER Visit' },
  { key: 'called_surgeon', label: 'Called Surgeon' },
];

export default function RecoveryStep({ data, update }) {
  const [numIncisions, setNumIncisions] = useState(0);
  const [activeDevices, setActiveDevices] = useState([]);

  useEffect(() => {
    client.get('/recovery/surgery-profile').then(res => {
      if (res.data && res.data.numIncisions) {
        setNumIncisions(res.data.numIncisions);
        if (!data.incisionObservations || data.incisionObservations.length === 0) {
          const obs = Array.from({ length: res.data.numIncisions }, (_, i) => ({
            index: i,
            redness: false,
            swelling: false,
            warmth: false,
            drainage: false,
            opening: false,
            notes: '',
          }));
          update({ incisionObservations: obs });
        }
      }
    }).catch(() => {});

    client.get('/recovery/drain-catheters', { params: { active: true } }).then(res => {
      setActiveDevices(res.data || []);
    }).catch(() => {});
  }, []);

  const toggleComplication = (key) => {
    const current = data.complications || [];
    if (current.includes(key)) {
      update({ complications: current.filter(c => c !== key) });
    } else {
      update({ complications: [...current, key] });
    }
  };

  const updateIncision = (index, field, value) => {
    const obs = [...(data.incisionObservations || [])];
    obs[index] = { ...obs[index], [field]: value };
    update({ incisionObservations: obs });
  };

  const updateDrainLog = (deviceId, field, value) => {
    const logs = [...(data.drainCatheterLogs || [])];
    const idx = logs.findIndex(l => l.drainCatheterId === deviceId);
    if (idx >= 0) {
      logs[idx] = { ...logs[idx], [field]: value };
    } else {
      logs.push({ drainCatheterId: deviceId, outputAmount: '', notes: '', [field]: value });
    }
    update({ drainCatheterLogs: logs });
  };

  return (
    <div className="space-y-6">
      <Slider
        label="Recovery Self-Assessment"
        value={data.recoverySelfAssessment || 1}
        onChange={(v) => update({ recoverySelfAssessment: v })}
        lowLabel="Very rough"
        highLabel="Great"
      />

      <Slider
        label="Incision Pain"
        value={data.incisionPainLevel || 1}
        onChange={(v) => update({ incisionPainLevel: v })}
        lowLabel="None"
        highLabel="Severe"
      />

      <Slider
        label="Shoulder / Gas Pain"
        value={data.shoulderGasPainLevel || 1}
        onChange={(v) => update({ shoulderGasPainLevel: v })}
        lowLabel="None"
        highLabel="Severe"
      />

      <div>
        <button
          type="button"
          onClick={() => update({ shoulderGasPainResolved: !data.shoulderGasPainResolved })}
          className={`w-full py-3 rounded-xl font-medium transition
            ${data.shoulderGasPainResolved
              ? 'bg-green-500/20 text-green-300 ring-2 ring-green-500/30'
              : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
        >
          {data.shoulderGasPainResolved ? 'Gas Pain Resolved' : 'Mark Gas Pain as Resolved'}
        </button>
      </div>

      {/* Incision Observations */}
      {numIncisions > 0 && (
        <div>
          <label className="block text-sm font-medium text-white/80 mb-3">Incision Observations</label>
          <div className="space-y-3">
            {(data.incisionObservations || []).map((obs, i) => (
              <div key={i} className="bg-white/5 rounded-xl p-3 space-y-2">
                <p className="text-sm font-medium text-white/70">Incision {i + 1}</p>
                <div className="flex flex-wrap gap-2">
                  {['redness', 'swelling', 'warmth', 'drainage', 'opening'].map(field => (
                    <button
                      key={field}
                      type="button"
                      onClick={() => updateIncision(i, field, !obs[field])}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition
                        ${obs[field]
                          ? 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
                          : 'bg-white/10 text-white/50 hover:bg-white/20'
                        }`}
                    >
                      {field.charAt(0).toUpperCase() + field.slice(1)}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Notes for this incision..."
                  value={obs.notes || ''}
                  onChange={e => updateIncision(i, 'notes', e.target.value)}
                  className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/30 text-sm px-3 py-1.5"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Complications */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Complications / Concerns</label>
        <div className="flex flex-wrap gap-2">
          {COMPLICATION_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleComplication(key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition
                ${(data.complications || []).includes(key)
                  ? 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {(data.complications || []).length > 0 && (
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Complication Notes</label>
          <textarea
            value={data.complicationNotes || ''}
            onChange={e => update({ complicationNotes: e.target.value })}
            placeholder="Describe any complications..."
            rows={3}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 shadow-sm focus:border-white/40 focus:ring-white/30"
          />
        </div>
      )}

      {/* Drain/Catheter Logs */}
      {activeDevices.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-white/80 mb-3">Drain / Catheter Output</label>
          <div className="space-y-3">
            {activeDevices.map(device => {
              const log = (data.drainCatheterLogs || []).find(l => l.drainCatheterId === device.id) || {};
              return (
                <div key={device.id} className="bg-white/5 rounded-xl p-3 space-y-2">
                  <p className="text-sm font-medium text-white/70">
                    {device.deviceType} (placed {device.placementDate})
                  </p>
                  <input
                    type="text"
                    placeholder="Output amount (e.g. 50ml)"
                    value={log.outputAmount || ''}
                    onChange={e => updateDrainLog(device.id, 'outputAmount', e.target.value)}
                    className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/30 text-sm px-3 py-1.5"
                  />
                  <input
                    type="text"
                    placeholder="Notes..."
                    value={log.notes || ''}
                    onChange={e => updateDrainLog(device.id, 'notes', e.target.value)}
                    className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/30 text-sm px-3 py-1.5"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
